import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Phone } from 'lucide-react';
import { ksbData } from '../data/ksb';
import PDFModal from '../components/PDFModal';
import './KSB.css';

const MonitorSvg = () => (
  <svg width="100%" height="100%" viewBox="0 0 400 200" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <rect width="400" height="200" fill="#1A3A5C" />
    <circle cx="200" cy="100" r="70" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
    <circle cx="200" cy="100" r="52" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
    <circle cx="200" cy="100" r="35" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
    <circle cx="200" cy="100" r="18" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
    <circle cx="200" cy="100" r="3.5" fill="rgba(255,255,255,0.3)" />
    <path d="M172 70 Q 160 52, 142 49" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M164 60 Q 154 46, 138 43" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M228 130 Q 240 148, 258 151" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M236 140 Q 246 154, 262 157" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="142" cy="62" r="3" fill="rgba(255,255,255,0.25)" />
    <circle cx="262" cy="138" r="3" fill="rgba(255,255,255,0.25)" />
    <circle cx="248" cy="55" r="2.5" fill="rgba(255,255,255,0.2)" />
    <circle cx="148" cy="145" r="2.5" fill="rgba(255,255,255,0.2)" />
    <circle cx="115" cy="105" r="2" fill="rgba(255,255,255,0.15)" />
    <circle cx="285" cy="95" r="2" fill="rgba(255,255,255,0.15)" />
    <line x1="142" y1="62" x2="182" y2="86" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
    <line x1="262" y1="138" x2="220" y2="114" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
    <line x1="248" y1="55" x2="214" y2="82" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
    <line x1="148" y1="145" x2="186" y2="116" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
    <line x1="115" y1="105" x2="165" y2="100" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
    <line x1="285" y1="95" x2="235" y2="100" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
  </svg>
);

const KSB = () => {
  const navigate = useNavigate();
  const [pdfModal, setPdfModal] = useState(null);
  const whatsappNumber = '5493413047536';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=¡Hola!%20Quiero%20consultar%20sobre%20productos%20KSB`;

  const pageData = ksbData.ksb;

  return (
    <>
    <div className="ksb-container" data-testid="ksb-page">
      {/* Hero Section */}
      <section className="ksb-hero">
        <div className="ksb-hero-content">
          <h1 className="ksb-hero-title">KSB</h1>
          {pageData.intro.map((text, idx) => (
            <p key={idx} className="ksb-hero-description">{text}</p>
          ))}
        </div>
        <div className="ksb-hero-image">
          <img
            src="/assets/ksb/ksb_header.webp"
            alt="KSB bombas y válvulas"
            loading="lazy"
          />
          <div className="ksb-hero-image-overlay" />
        </div>
      </section>

      {/* Cards Section */}
      <section className="ksb-cards-section">
        <div className="container">
          <div className="ksb-cards-grid">
            {pageData.sections[0].cards.map((card, idx) => (
              <Card
                key={idx}
                className="ksb-card"
                data-testid={`card-${card.title.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`}
                onClick={() => {
                  if (card.isPdfModal) setPdfModal({ url: card.href, title: card.title });
                  else navigate(card.href);
                }}
              >
                <CardContent className="ksb-card-content">
                  <div className="ksb-card-image">
                    {card.svgType === 'monitor' ? (
                      <MonitorSvg />
                    ) : (
                      <img
                        src={card.image}
                        alt={card.title}
                        loading="lazy"
                        className="card-image"
                      />
                    )}
                  </div>
                  <h3 className="ksb-card-title">{card.title}</h3>
                  <p className="ksb-card-description">{card.description}</p>
                  <span className="ksb-card-arrow">→</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ksb-cta-section">
        <div className="container">
          <p className="ksb-cta-text">
            Representamos tecnología que potencia la eficiencia y confiabilidad de tus procesos.
          </p>
          <Button 
            size="lg" 
            className="ksb-cta-button"
            onClick={() => window.open(whatsappLink, '_blank')}
            data-testid="ksb-contact-button"
          >
            <Phone className="mr-2" size={20} />
            Contactanos
          </Button>
        </div>
      </section>
    </div>

    {pdfModal && (
      <PDFModal
        pdfUrl={pdfModal.url}
        title={pdfModal.title}
        onClose={() => setPdfModal(null)}
      />
    )}
    </>
  );
};

export default KSB;
